<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="executive-dropoff-bench"
      class="mb-2"
    />
    <ChartContent
      height="131px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBenchmarkChart"
      class="px-0 pb-0"
      :link="flags.hideDropOff ? null : { name: 'Drop-off Report', to: 'drop-off' }"
    >
      <BarChart :id="`${widgetId}-chart`" :data="chartData" :options="chartOptions" :plugins="plugins" />
      <template #error>
        <UnavailableWidget v-if="flags.hideDropOff" img-name="faded-benchmark">
          Drop-off data is not available in your account
          due to potential data integrity issues.
          <router-link to="drop-off" target="_blank" rel="noopener noreferrer">
            Click here for more info.
          </router-link>
        </UnavailableWidget>
      </template>
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CanvasDrawBenchmarkPlugin } from '@/api/helpers/insights/chartPlugins';
import { formatTooltipValue } from '@/helpers/formatters';
import UnavailableWidget from '@/components/insights/UnavailableWidget.vue';
import { basicChartTooltipPluginConfig, benchmarkPluginPercentOffset, hiddenYScaleConfig } from '@/api/helpers/insights/commonChartConfigs';

const DROP_OFF_MAX = 5;
const DROP_OFF_MIN = 2.5;

const props = defineProps({
  dropOffRate: {
    type: Number,
    required: true,
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  flags: {
    type: Object,
    default: () => ({}),
  },
});

const widgetLabel = 'Drop-off Rate vs Benchmark';

const widgetId = 'drop-off-rate-benchmark';

const {
  brandPrimary,
  yellow500,
  grey400,
  textDarkest,
  white,
} = useColors();

const noWidgetData = computed(() => !props.dropOffRate);

const realChartMax = computed(() => {
  if (props.dropOffRate >= 15) {
    if (props.dropOffRate % 5 === 0) return props.dropOffRate + 5;
    return (Math.ceil(props.dropOffRate / 5) * 5) + 5;
  }

  return 15;
});

const chartData = computed(() => {
  const { dropOffRate } = props || {};

  return {
    labels: ['Drop-off Rate'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        borderColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          dropOffRate,
        ],
      },
    ],
  };
});

const plugins = computed(() => [
  ChartDataLabels,
  CanvasDrawBenchmarkPlugin({
    benchmarkOptions: {
      min: DROP_OFF_MIN,
      max: DROP_OFF_MAX,
      ...benchmarkPluginPercentOffset,
    },
    scaleMax: realChartMax.value,
    chartId: `${widgetId}-chart`,
  }),
]);

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: basicChartTooltipPluginConfig(chartData, widgetId),
    datalabels: {
      color: function colorCallback(context) {
        const max = realChartMax.value;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        return percentOfSpace < 20 ? textDarkest.value : white.value;
      },
      anchor: 'end',
      align: function alignCallback(context) {
        const max = realChartMax.value;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        if (Number.isNaN(percentOfSpace)) return 'end';

        return percentOfSpace < 20 ? 'end' : 'start';
      },
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';
        return formatTooltipValue(value, 'percent');
      },
    },
  },
  scales: {
    x: {
      max: realChartMax.value,
      title: {
        display: false,
      },
      beginAtZero: true,
      border: {
        display: false,
        dash: [2, 3],
      },
      grid: {
        display: true,
        color: grey400.value,
        tickLength: 0,
      },
      ticks: {
        padding: 24,
        maxRotation: 0,
        stepSize: 5,
        callback: function xTickCallback(value) {
          return `${value}%`;
        },
      },
    },
    y: hiddenYScaleConfig,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 131px !important;
  max-height: 131px !important;
  padding: 16px 0px !important;
  margin: 0 !important;
}
</style>
